"use client";

import {
  COOKIE_KEY_HLW_CONSENT,
  getDeviceIsMobile,
  getDeviceType,
  getDeviceUserAgent,
  getFullName,
  useAccessibility,
  useCookie,
  useRequestIp,
  useRequestMe,
  useSession,
  z,
} from "@packages/sdk";
import { useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import { Suspense, useEffect, useState } from "react";

import type { AnalyticsContext } from "../lib";
import { analytics, analyticsContext, DEFAULT_CONSENT_OPTIONS } from "../lib";
import type { AnalyticsEvent } from "../types";
import { Consent } from "../types";
import { getIpIsStrictConsentPermissions } from "../utils";

export type AnalyticsProviderProps = Omit<
  ComponentProps<typeof analyticsContext.Provider>,
  "value"
>;

const Provider = ({ children, ...props }: AnalyticsProviderProps) => {
  const [isIdentified, setIsIdentified] = useState(false);

  const searchParams = useSearchParams();

  const { hlwFontStyle, hlwLetterSpacing, hlwWordSpacing, preferredTheme } =
    useAccessibility();
  const [consent, setConsent] = useCookie<number[]>({
    key: COOKIE_KEY_HLW_CONSENT,
    schema: z.number().array(),
  });
  const { mutationGet: mutationGetIp } = useRequestIp();
  const { mutationPut: mutationPutMe, query: queryMe } = useRequestMe();
  const { status } = useSession();

  const isConsenting = consent ? consent.includes(Consent.ANALYTICS) : false;
  const isWindowExists = typeof window !== "undefined";

  const logout = () => analytics?.reset();

  /** TODO: remove/improve when ConsentModal/CookieManager are improved */
  const [isConsentModalVisible, setIsConsentModalVisible] = useState(false);
  const saveConsent = async ({
    consent,
  }: {
    consent: AnalyticsContext["consent"];
  }) => {
    setConsent(consent);
    if (status === "authenticated")
      await mutationPutMe.mutateAsync({ consent });
  };

  const track = ({ event, properties }: AnalyticsEvent) =>
    analytics.track(event, {
      browser: getDeviceUserAgent(),
      device_type: getDeviceType(),
      font_style: hlwFontStyle,
      letter_spacing: hlwLetterSpacing ? "expanded" : "default",
      mobile: getDeviceIsMobile(),
      platform: "web",
      referrer: searchParams.get("referrer"),
      screen_height: isWindowExists ? window?.innerHeight : 0,
      /** TODO: define getScreenName() */
      screen_name: isWindowExists ? window?.location.pathname : "",
      screen_width: isWindowExists ? window?.innerWidth : 0,
      slug: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      theme: preferredTheme,
      utm_campaign: searchParams.get("utmCampaign"),
      utm_medium: searchParams.get("utmMedium"),
      utm_source: searchParams.get("utmSource"),
      word_spacing: hlwWordSpacing ? "expanded" : "default",
      ...properties,
    }) as unknown as Promise<void>;

  /**
   * If the user is consenting, load analytics
   * If the user is not consenting and has consent preferences, show consent modal
   * If the user is not consenting and does not have consent preferences,
   * default: use DEFAULT_CONSENT_OPTIONS
   * strict IP: show consent modal
   */
  useEffect(() => {
    const handleConsentFromIp = async () => {
      const ip = await mutationGetIp.mutateAsync();

      if (ip) {
        const ipIsStrictConsentPermissions = getIpIsStrictConsentPermissions({
          ip,
        });
        if (ipIsStrictConsentPermissions) setIsConsentModalVisible(true);
        else setConsent(DEFAULT_CONSENT_OPTIONS);
      }
    };

    if (isConsenting)
      analytics.load({ writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY });
    else if (consent) setIsConsentModalVisible(true);
    else handleConsentFromIp();
  }, [isConsenting, consent]);

  /** Sync any inconsistent consents */
  useEffect(() => {
    if (queryMe.data) {
      if (queryMe.data?.consent) {
        if (JSON.stringify(consent) !== JSON.stringify(queryMe.data.consent))
          setConsent(queryMe.data.consent);
      } else if (consent) mutationPutMe.mutate({ consent });
    }
  }, [consent, queryMe.data?.consent]);

  /**
   * If user authenticates, sync pre-auth and post-auth analytics
   * Segment analytics.identify() syncs events
   */
  useEffect(() => {
    if (isConsenting && queryMe.data?.id && !isIdentified) {
      analytics.identify(queryMe.data.id.toString(), {
        consent,
        email: queryMe.data?.email,
        locale: queryMe.data?.language,
        name: getFullName({
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
        }),
        phoneNumber: queryMe.data?.phone,
      });
      setIsIdentified(true);
    }
  }, [queryMe.data?.id]);

  return (
    <analyticsContext.Provider
      value={{
        analytics,
        consent,
        isConsentModalVisible,
        logout,
        saveConsent,
        setConsent,
        setIsConsentModalVisible,
        track,
      }}
      {...props}
    >
      {children}
    </analyticsContext.Provider>
  );
};

export const AnalyticsProvider = ({
  children,
  ...props
}: AnalyticsProviderProps) => (
  <Suspense>
    <Provider {...props}>{children}</Provider>
  </Suspense>
);
